import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Modal,
  Chip,
  Autocomplete,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { sharePlannerDetails } from "../../../api/preneed-planners.api";
import { sharePartnerEmail } from "../../../api/details-page.api";
import { joiEmailSchema } from "../../../utils/utils";
import "./SharePartnerPopup.scss";

interface SharePartnerPopupProps {
  open: boolean;
  onClose: () => void;
  partnerTitle: string;
  onSendEmail?: (email: string) => void;
  shareLink?: string;
  isPlanner?: boolean;
  providerId: number;
}

const SharePartnerPopup: React.FC<SharePartnerPopupProps> = ({
  open,
  onClose,
  partnerTitle,
  onSendEmail,
  shareLink = window.location.href,
  isPlanner,
  providerId,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [successfulMsg, setSuccessfulMsg] = useState("");
  const [copyDisabled, setCopyDisabled] = useState(false);
  const [copyText, setCopyText] = useState("Copy Link");
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [emailError, setEmailError] = useState("");

  const validateEmail = (value: string) => {
    const { error } = joiEmailSchema.validate(value);
    return !error;
  };

  const handleSend = async () => {
    if (!emails.length) return;

    setLoading(true);

    try {
      const response = isPlanner
        ? await sharePlannerDetails(providerId, { emails: emails })
        : await sharePartnerEmail(providerId, { emails: emails });

      if (response.data?.success) {
        setSuccessfulMsg("Shared Successfully");
        setEmails([]);
        setInputValue("");

        setTimeout(() => {
          setSuccessfulMsg("");
        }, 3000);
      }
    } catch (error) {
      console.error("ERROR SHARING PARTNER EMAIL::", error);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareLink);
    setCopyText("Link Copied");
    setCopyDisabled(true);

    // reset button after 3 seconds
    setTimeout(() => {
      setCopyText("Copy Link");
      setCopyDisabled(false);
    }, 3000);
  };

  useEffect(() => {
    if (!open) {
      setEmails([]);
      setInvalidEmails([]);
      setEmailError("");
      setSuccessfulMsg("");
      setInputValue("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
    >
      <Box className="share-partner-popup">
        {/* Header */}
        <Box className="share-partner-popup__header">
          <Typography className="share-partner-popup__title">
            {partnerTitle}
          </Typography>
          <IconButton
            className="share-partner-popup__close-btn"
            onClick={onClose}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Box>

        {/* Description */}
        <Typography className="share-partner-popup__subtitle">
          Send this {isPlanner ? "Planner" : "Provider"} via email or copy the
          link to share instantly.
        </Typography>

        {/* Success Message */}
        {successfulMsg && (
          <Typography className="share-partner-popup__success-msg">
            {successfulMsg}
          </Typography>
        )}

        {/* Input Label */}
        <Typography className="share-partner-popup__label">
          Enter Email ID
        </Typography>

        {/* Input */}
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={emails}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onChange={(event, newValue) => {
            const invalids = newValue.filter((email) => !validateEmail(email));

            setEmails(newValue);
            setInvalidEmails(invalids);

            if (invalids.length) {
              setEmailError(
                "One or more email addresses are invalid. Please correct them before Sharing."
              );
            } else {
              setEmailError("");
            }
          }}
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option, index) => {
              return (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  sx={{
                    color: "#FFFFFF",
                    backgroundColor: "#1B76D2",
                    "& .MuiChip-deleteIcon": {
                      color: "#fff",
                      opacity: 0.9,
                    },
                  }}
                />
              );
            })
          }
          filterOptions={(x) => x}
          popupIcon={null}
          disableCloseOnSelect
          PopperComponent={(props) => <></>}
          renderInput={(params) => (
            <Box>
              <TextField
                {...params}
                variant="outlined"
                placeholder="Enter an email address and press Enter to add it."
                size="small"
                error={Boolean(emailError)}
                className="share-partner-popup__input"
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "#ccc !important",
                  },
                }}
              />
              <Typography
                sx={{
                  textAlign: "right",
                  color: "#d32f2f",
                  fontSize: "14px",
                }}
              >
                {emailError}
              </Typography>
            </Box>
          )}
        />

        {/* Actions */}
        <Box className="share-partner-popup__actions">
          <Button
            variant="outlined"
            onClick={handleCopy}
            disabled={copyDisabled}
            className="share-partner-popup__btn share-partner-popup__btn--copy"
          >
            {copyText}
          </Button>

          <Button
            onClick={handleSend}
            disabled={!emails.length || invalidEmails.length > 0}
            className="share-partner-popup__btn share-partner-popup__btn--share"
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Share"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SharePartnerPopup;
